import { loadStripe } from "@stripe/stripe-js";
import { GetterTree, Module } from "vuex";

import { CheckoutApi } from "@/api/checkout";
import { PatientsApi } from "@/api/patients";
import router from "@/router";
import { RootState } from "@/types";

import { state } from "./state";
import { CheckoutResponseDto, CheckoutState, Medication, MutationPayload, PMH } from "./types";

export const checkout: Module<CheckoutState, RootState> = {
  namespaced: true,
  state,
  mutations: {
    setStringFieldByName(state: CheckoutState, payload: MutationPayload) {
      const { fieldName, value } = payload;
      state[fieldName] = value;
    },
    setMedication(state: CheckoutState, payload: Medication[]) {
      state.medications = payload;
    },
    setAllergies(state: CheckoutState, payload: string[]) {
      state.allergies = payload;
    },
    setPMH(state: CheckoutState, payload: PMH[]) {
      state.pmh = payload;
    },
    setIsSmoking(state: CheckoutState, payload: boolean) {
      if (payload) {
        state.isSmoking = payload;
      }
    },
    setVisitDate(state: CheckoutState, payload: Date) {
      state.visitDate = payload;
    },
    setConfirmedAccurace(state: CheckoutState, payload: boolean) {
      state.confirmedAccurace = payload;
    },
    setSession(state: CheckoutState, payload) {
      state.session = payload;
    },
  },
  actions: {
    async submitSecondStep(ctx) {
      const {
        slotId,
        chiefComplaint,
        complaintDescription,
        height,
        weight,
        isSmoking,
        allergies,
        medications,
        pmh,
        visitDate,
        confirmedAccurace,
      } = ctx.state;
      const { uid } = ctx.rootGetters;
      const checkout = await CheckoutApi.create({
        slotId,
        patientId: uid,
        information: {
          confirmedAccurace,
          visitDate,
          chiefComplaint,
          complaintDescription,
          generalInformation: {
            height,
            weight,
            isSmoking,
          },
          medicalInformation: {
            allergies,
            pmh,
            medications,
          },
        },
      });
      await ctx.dispatch("completeCheckout", checkout);
    },
    async submitEditMode(ctx) {
      const {
        slotId,
        chiefComplaint,
        complaintDescription,
        height,
        weight,
        isSmoking,
        allergies,
        medications,
        pmh,
        visitDate,
        confirmedAccurace,
        session,
      } = ctx.state;
      const { uid } = ctx.rootGetters;
      const checkout = await CheckoutApi.update(
        {
          slotId,
          patientId: uid,
          information: {
            confirmedAccurace,
            visitDate,
            chiefComplaint,
            complaintDescription,
            generalInformation: {
              height,
              weight,
              isSmoking,
            },
            medicalInformation: {
              allergies,
              pmh,
              medications,
            },
          },
        },
        session.id,
      );
      await ctx.dispatch("completeCheckout", checkout);
    },
    async completeCheckout(ctx, checkout: CheckoutResponseDto) {
      const { id, price } = checkout;
      if (price > 0) {
        const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY || "");
        if (stripe) {
          await stripe.redirectToCheckout({
            sessionId: id,
          });
        }
      } else {
        await CheckoutApi.complete(id);
        await router.push({
          name: `patientCheckoutComplete`,
          params: {
            sessionId: id,
            status: "success",
          },
        });
      }
    },
    async practitionerRequestVisit(ctx) {
      const { slotId, patientId, chiefComplaint, complaintDescription, visitDate } = ctx.state;
      await CheckoutApi.create({
        slotId,
        patientId,
        information: {
          chiefComplaint,
          complaintDescription,
          visitDate,
        },
      });
    },
    async ownerRequestVisit(ctx) {
      const {
        slotId,
        patientId,
        chiefComplaint,
        complaintDescription,
        visitDate,
        height,
        weight,
        isSmoking,
        allergies,
        pmh,
        medications,
        reassignPractitionerId,
        reassign,
      } = ctx.state;
      await CheckoutApi.create({
        slotId,
        patientId,
        information: {
          chiefComplaint,
          complaintDescription,
          visitDate,
          generalInformation: {
            height,
            weight,
            isSmoking,
          },
          medicalInformation: {
            allergies,
            pmh,
            medications,
          },
        },
        ...(reassign && { reassignPractitionerId }),
      });
    },
    async preloadPatientProfile({ rootGetters, commit }) {
      const { uid } = rootGetters;
      const settings = await PatientsApi.getSettings(uid);
      const { medicalInformation, generalInformation } = settings;
      if (medicalInformation) {
        commit("setPMH", medicalInformation.pmh || []);
        commit("setAllergies", medicalInformation.allergies);
        commit("setMedication", medicalInformation.medications);
      }
      if (generalInformation) {
        commit("setStringFieldByName", { value: generalInformation?.height, fieldName: "height" });
        commit("setStringFieldByName", { value: generalInformation?.weight, fieldName: "weight" });
        commit("setIsSmoking", generalInformation.isSmoking);
      }
    },
    async getSessionInfo({ commit }, sessionId: string) {
      const { slotId, status, expectedRejectDate, information, id, price } = await CheckoutApi.getById(sessionId);
      const priceInDollars = `$${parseInt(price, 10) / 100}`;
      commit("setSession", { status, rejectDate: expectedRejectDate, id, price: priceInDollars });
      commit("setStringFieldByName", { value: slotId, fieldName: "slotId" });
      commit("setStringFieldByName", { value: information.chiefComplaint, fieldName: "chiefComplaint" });
      commit("setStringFieldByName", { value: information.generalInformation?.height, fieldName: "height" });
      commit("setStringFieldByName", { value: information.generalInformation?.weight, fieldName: "weight" });
      commit("setMedication", information.medicalInformation?.medications);
      commit("setAllergies", information.medicalInformation?.allergies);
      commit("setPMH", information.medicalInformation?.pmh);
      commit("setVisitDate", information.visitDate || null);
      commit("setConfirmedAccurace", information.confirmedAccurace);
      commit("setIsSmoking", information.generalInformation?.isSmoking);
      commit("setStringFieldByName", {
        value: information.complaintDescription,
        fieldName: "complaintDescription",
      });
    },
    clearAllFields({ commit }) {
      commit("setStringFieldByName", { value: "", fieldName: "slotId" });
      commit("setStringFieldByName", { value: "", fieldName: "chiefComplaint" });
      commit("setStringFieldByName", { value: "", fieldName: "height" });
      commit("setStringFieldByName", { value: "", fieldName: "weight" });
      commit("setStringFieldByName", { value: "", fieldName: "patientId" });
      commit("setMedication", []);
      commit("setAllergies", []);
      commit("setPMH", []);
      commit("setVisitDate", null);
      commit("setConfirmedAccurace", false);
      commit("setStringFieldByName", {
        value: "",
        fieldName: "complaintDescription",
      });
    },
  },
  getters: {
    ...Object.keys(state).reduce((acc: GetterTree<CheckoutState, RootState>, fieldName: string) => {
      acc[fieldName] = (state: CheckoutState) => state[fieldName];
      return acc;
    }, {}),
  },
};
